// Practice choices carry a `default_depth` for the implement/method they
// describe, which pre-fills the depth column when that choice is selected.
//
// The fertilizer method "injected" is the one choice whose depth is not a
// property of the method alone -- organic injection is a manure injector,
// synthetic an anhydrous knife -- so it arrives with `default_depth` null and a
// `default_depth_by_category` pair instead.
export function getDefaultDepth(choices, value, category = null) {
  const choice = findPracticeChoice(choices, value)
  if (!choice) return null
  if (choice.default_depth_by_category) {
    if (category == null) return null
    return choice.default_depth_by_category[normalizeChoice(category)] ?? null
  }
  return choice.default_depth ?? null
}

// True when the choice's depth depends on a second selection, so callers know to
// re-derive the depth when that other selection changes.
export function isDepthCategoryDependent(choices, value) {
  return Boolean(findPracticeChoice(choices, value)?.default_depth_by_category)
}

// Planting, fertilizer and cover crop record depth in inches, matching the
// T-DISC implement table the default depths come from, so the unit follows the
// value rather than being chosen. Tillage predates this and keeps its own
// `unit` (in / cm).
export const DEPTH_UNIT = "in"

// Whether a depth can meaningfully be entered for a selection, used to disable
// both the depth input and its unit. False in the two cases where there is no
// depth to give: the option carries no depth at all (a surface fertilizer
// application), or it carries a depth of zero ("None", and broadcast seeding,
// which sits on the surface).
//
// Only current options are locked down. "Other", an unrecognised value, and a
// legacy option all resolve to no implement, but a row on one of those still
// holds a real depth the grower entered, and there is no other way to capture or
// correct it -- so those stay editable.
export function isDepthApplicable(choices, value) {
  if (value == null) return true
  const choice = findPracticeChoice(choices, value)
  if (!choice) return true
  if (choice.selectable === false) return true
  if (normalizeChoice(choice.value) === "other") return true
  if (choice.default_depth_by_category) return true
  return choice.default_depth != null && choice.default_depth !== 0
}

// Options that predate the T-DISC implement table come back `selectable: false`.
// They stay in the list so a row saved with one still renders its stored value,
// but they are greyed out -- Vuetify reads `disabled` off each item by default --
// so editing an old row can only move it forward onto a mapped option.
export function disableUnselectable(choices) {
  if (!choices) return choices
  return choices.map(c => (c.selectable === false ? { ...c, disabled: true } : c))
}

// Match a stored selection to its choice by slug *or* display name. Both forms
// are in circulation in saved data -- the slug is what the dropdowns store now,
// the label is what older imports wrote -- so a row saved as "Injected" has to
// resolve to the same choice as one saved as "injected". Mirrors the folding
// the T-DISC calculator applies server-side, so the depth shown here is the
// depth the CI score is computed from.
function findPracticeChoice(choices, value) {
  if (!choices || value == null) return null
  const token = normalizeChoice(value)
  if (!token) return null
  return (
    choices.find(
      c =>
        normalizeChoice(c.value) === token
        || (c.display_name != null && normalizeChoice(c.display_name) === token)
    ) ?? null
  )
}

function normalizeChoice(name) {
  return String(name)
    .toLowerCase()
    .replace(/[_\-/]/g, " ")
    .replace(/[(),.;'"]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ")
}

export function getDataCompletionObjectForYear(defaults_data_completion, year) {
  if (!defaults_data_completion) return {}

  const ddc = {}
  for(const key in defaults_data_completion) {
    if(key == "id") continue
    const item = defaults_data_completion[key]
    ddc[key] = getItemForYear(item, year)
  }
  return ddc
}

function getItemForYear(data, year) {
  const completion = data.by_year.find(
    item => item.year == parseInt(year)
  )
  return completion
}
